/**
 * Studio Admin search (Cmd+K / Ctrl+K, or the Search button at the top of the
 * sidebar): paste a run, session, project, organization or MicroVM id, or type
 * an organization name or a RapidSOS person's email, and open the page for it.
 * Platform admins only; for anyone else the provider renders just its children.
 */
import { Loader2, Search } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AdminLookupResult } from '@/api/adminClient';
import { LookupResults } from '@/components/search/LookupResults';
import { Dialog } from '@/components/ui/dialog';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useAdminLookup } from '@/hooks/useAdminLookup';
import { isSearchShortcut, moveHighlight, orderedLookupResults, searchShortcutLabel } from '@/lib/adminSearch';

type CommandPaletteControls = { available: boolean; open: () => void; shortcut: string };

const CommandPaletteContext = createContext<CommandPaletteControls>({ available: false, open: () => undefined, shortcut: '' });

export function useCommandPalette(): CommandPaletteControls {
  return useContext(CommandPaletteContext);
}

const LIST_ID = 'admin-search-results';

function PaletteBody({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const lookup = useAdminLookup(query);
  const ordered = useMemo(() => orderedLookupResults(lookup.results), [lookup.results]);

  useEffect(() => {
    setHighlighted(ordered.length > 0 ? 0 : -1);
  }, [ordered]);

  const open = (result: AdminLookupResult | undefined) => {
    if (!result) return;
    onClose();
    navigate(result.path);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => moveHighlight(current, event.key === 'ArrowDown' ? 1 : -1, ordered.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      open(ordered[highlighted]);
    }
  };

  const typed = query.trim().length >= 2;
  const settled = typed && !lookup.loading && lookup.query === query.trim();

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Paste an id, or type an organization or person"
          aria-label="Search Studio Admin"
          aria-controls={LIST_ID}
          aria-activedescendant={highlighted >= 0 && ordered.length > 0 ? `${LIST_ID}-${highlighted}` : undefined}
          autoComplete="off"
          spellCheck={false}
          className="h-11 w-full rounded-lg border border-input bg-background pl-9 pr-9 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {lookup.loading ? (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" aria-label="Searching" />
        ) : null}
      </div>
      <div className="max-h-[55vh] min-h-[3rem] overflow-y-auto">
        {!typed ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            Works with run, session, project, organization, MicroVM and lease ids, organization names, and RapidSOS people by email or name.
          </p>
        ) : lookup.error ? (
          <p className="px-2 py-3 text-sm text-destructive-foreground">Search didn't work: {lookup.error}</p>
        ) : ordered.length > 0 ? (
          <LookupResults results={ordered} highlighted={highlighted} onSelect={open} onHighlight={setHighlighted} listId={LIST_ID} />
        ) : settled ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Nothing matches “{query.trim()}”. Check the id is complete, or try a name.</p>
        ) : null}
      </div>
      <p className="hidden items-center gap-3 px-2 text-[11px] text-muted-foreground sm:flex">
        <span>↑ ↓ to move</span>
        <span>Enter to open</span>
        <span>Esc to close</span>
      </p>
    </div>
  );
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const { isPlatformAdmin } = useAdminAccess();
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const shortcut = useMemo(() => searchShortcutLabel(), []);

  useEffect(() => {
    if (!isPlatformAdmin) return undefined;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      setIsOpen((current) => !current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPlatformAdmin]);

  const controls = useMemo(() => ({ available: isPlatformAdmin, open, shortcut }), [isPlatformAdmin, open, shortcut]);

  return (
    <CommandPaletteContext.Provider value={controls}>
      {children}
      {isPlatformAdmin ? (
        <Dialog open={isOpen} onClose={close} title="Search" className="max-w-xl">
          <PaletteBody onClose={close} />
        </Dialog>
      ) : null}
    </CommandPaletteContext.Provider>
  );
}
